import { Clear, Tune } from "@mui/icons-material";
import { Button, ClickAwayListener, Divider, Fade, Paper, Popper, Stack, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { CHECK_ORDER_BULK_PREPARE, ORDER_SOURCE_TYPE, orderStatusFormatted, RETURN_REFUND_OPTIONS } from "src/constant";
import useTPList from "src/hooks/setting/transportation-provider/useTPList";
import useQueryParams from "src/hooks/useQueryParams";
import BtnGroupDynamic from "./BtnGroupDynamic";
import useAuthGlobal from "src/context/useAuthGlobal";
import { STATUS } from "src/pages/debt/const/constants";

const params = { limit: 200, offset: 0 };
const printStatus = [
  { _id: "notFinished", name: "Chưa in" },
  { _id: "finished", name: "Đã in" },
];
const sources = [
  { _id: ORDER_SOURCE_TYPE.Manual, name: ORDER_SOURCE_TYPE.Manual },
  { _id: ORDER_SOURCE_TYPE.SalesChannel, name: ORDER_SOURCE_TYPE.SalesChannel },
];
// const statusOutList = [
//   {
//     _id: orderStatusFormatted.Pending,
//     name: "Chờ xuất hàng",
//   },
//   {
//     _id: orderStatusFormatted.Exported,
//     name: "Xuất kho",
//   },
//   {
//     _id: orderStatusFormatted.Canceled,
//     name: "Hủy",
//   },
// ];

export default function AdvanceFilter({ setParamsQuery, paramsQuery, hasNCC = false }) {
  const pathObj = useLocation();
  const { userMe } = useAuthGlobal();
  const [option, setOption] = useQueryParams();
  const [popperProps, setPopperProps] = useState({
    open: false,
    anchorEl: null,
    placement: "bottom-end",
  });

  const { data: tpList } = useTPList(pathObj?.pathname?.includes("warehouse-management") ? undefined : params);

  const isInventoryInPath = pathObj.pathname?.includes("inventory-in");

  const handlePickOptionSearch = ({ name, value }) => {
    if (isInventoryInPath && value === ORDER_SOURCE_TYPE.All && (name === "fromWarehouse" || name === "provider")) {
      setParamsQuery({
        ...paramsQuery,
        fromWarehouse: undefined,
        provider: undefined,
        incomingFromSource: name === "fromWarehouse" ? "Warehouse" : "Provider",
      });
      return;
    }

    const updatedParams = {
      ...paramsQuery,
      [name]: value === ORDER_SOURCE_TYPE.All ? undefined : value,
    };

    if (name === "provider") {
      updatedParams.fromWarehouse = undefined;
    } else if (name === "fromWarehouse") {
      updatedParams.provider = undefined;
    }

    if (isInventoryInPath && (name === "fromWarehouse" || name === "provider") && value !== ORDER_SOURCE_TYPE.All) {
      delete updatedParams.incomingFromSource;
    }

    if (name === "warehouse") {
      setOption({ ...option, whouseId: name === "warehouse" ? value : undefined });
    }

    setParamsQuery(updatedParams);
  };

  const isTatCaActiveForFilterGroup = (groupName) => {
    if (!isInventoryInPath || (groupName !== "fromWarehouse" && groupName !== "provider")) {
      return (groupName === "provider" || groupName === "fromWarehouse") && (paramsQuery?.provider || paramsQuery?.fromWarehouse)
        ? paramsQuery?.[groupName] === ORDER_SOURCE_TYPE.All
        : [ORDER_SOURCE_TYPE.All, undefined]?.includes(paramsQuery?.[groupName]);
    }
    if (groupName === "fromWarehouse") {
      if (paramsQuery?.fromWarehouse) return false;
      if (paramsQuery?.provider) return false;
      if (paramsQuery?.incomingFromSource === "Warehouse") return true;
      if (paramsQuery?.incomingFromSource === "Provider") return false;
      return true;
    }
    if (groupName === "provider") {
      if (paramsQuery?.provider) return false;
      if (paramsQuery?.fromWarehouse) return false;
      if (paramsQuery?.incomingFromSource === "Provider") return true;
      if (paramsQuery?.incomingFromSource === "Warehouse") return false;
      return true;
    }
    return [ORDER_SOURCE_TYPE.All, undefined]?.includes(paramsQuery?.[groupName]);
  };

  /**
   * Key trong paramsQuery cho nút "Tất cả" / trạng thái active — phải trùng BtnGroupDynamic.
   * Kho Xuất: row `name` là warehouseOut nhưng BtnGroupDynamic ghi `warehouse`.
   */
  const advanceFilterTatCaParamKey = (v) => {
    if (v?.name === "warehouse" && !v?.isShowAll) return "whouseId";
    if (v?.id === "warehouseOut") return "warehouse";
    return v?.name;
  };

  /** Công nợ sales: các tab dùng lọc Kho xuất / Kho nhập (API inventory-notes hoặc payment-logs). */
  const debtSalesSubTabsWarehouseInOut = ["all", "unpaid", "overdue", "history"];

  const AdvanceFilterList = [
    {
      id: "warehouse",
      title: "Kho",
      name: "warehouse",
      // Trang /user dùng filter "Kho" riêng (user-employee-warehouse), tránh hai dòng trùng
      display: pathObj?.pathname?.includes("user") ? false : !!userMe?.warehouse ? false : true,
    },
    {
      id: "source",
      title: "Nguồn",
      name: "source",
      data: sources,
      disabled: [orderStatusFormatted.Manual, orderStatusFormatted.ReturnRefund]?.includes(option?.tab),
      display: pathObj?.pathname === "/orders" && option?.tab !== orderStatusFormatted?.DeliveredFailed,
    },
    {
      id: "tp",
      name: "shippingProvider",
      title: "Đơn vị vận chuyển",
      data: Array.isArray(tpList?.data) ? tpList.data : [],
      display: ["warehouse-management", "product"]?.includes(pathObj?.pathname) || (pathObj?.pathname === "/orders" && option?.tab !== orderStatusFormatted?.DeliveredFailed),
    },
    {
      id: "print",
      name: "isPrint",
      title: "Trạng thái in",
      data: printStatus,
      display: ["warehouse-management", "product"]?.includes(pathObj?.pathname),
    },
    {
      id: "warehouseIn",
      name: "statusIn",
      title: "Trạng thái",
      display:
        pathObj?.pathname?.includes("warehouse-management") ||
        (pathObj?.pathname === "/orders" && ![orderStatusFormatted?.DeliveredFailed, CHECK_ORDER_BULK_PREPARE.prepareBulk]?.includes(option?.tab)),
      data: [
        { _id: orderStatusFormatted.Pending, name: "Chờ nhập kho" },
        { _id: orderStatusFormatted.Imported, name: "Đã nhập kho" },
      ],
    },
    hasNCC && {
      id: "fromWarehouse",
      name: "fromWarehouse",
      title: "Kho xuất",
      display: pathObj?.pathname?.includes("warehouse-management") || (!pathObj?.pathname?.includes("inventory-in") && !pathObj?.pathname?.includes("product")),
      comp: (
        <BtnGroupDynamic
          handlePickOptionSearch={handlePickOptionSearch}
          paramsQuery={paramsQuery}
          path="/api/v1/warehouses"
          name="fromWarehouse"
          params={{ limit: 100, offset: 0 }}
        />
      ),
    },
    hasNCC && {
      id: "provider",
      name: "provider",
      title: "NCC",
      display: pathObj?.pathname?.includes("warehouse-management"),
      comp: (
        <BtnGroupDynamic
          handlePickOptionSearch={handlePickOptionSearch}
          paramsQuery={paramsQuery}
          path="/api/v1/providers"
          name="provider"
          params={{ limit: 100, offset: 0 }}
        />
      ),
    },
    {
      id: "paymentMethod",
      name: "paymentMethod",
      title: "Hình thức thanh toán",
      display: pathObj?.pathname?.includes("warehouse-management") || pathObj?.pathname?.includes("debt"),
      comp: (
        <BtnGroupDynamic
          handlePickOptionSearch={handlePickOptionSearch}
          paramsQuery={paramsQuery}
          path="/api/v1/payment-methods"
          name="paymentMethod"
          params={{ limit: 100, offset: 0 }}
        />
      ),
    },
    {
      id: "statusOut",
      name: "type",
      title: "Hình thức",
      display: pathObj?.pathname?.includes("inventory-in"),
      data: [
        { _id: RETURN_REFUND_OPTIONS.NEW, name: "Nhập mới" },
        { _id: RETURN_REFUND_OPTIONS.RETURN, name: "Hàng trả" },
        { _id: RETURN_REFUND_OPTIONS.Remove, name: "Xuất bỏ" },
      ],
    },
    {
      id: "brand",
      name: "brand",
      title: "Thương hiệu",
      display: pathObj?.pathname?.includes("product"),
      comp: (
        <BtnGroupDynamic
          handlePickOptionSearch={handlePickOptionSearch}
          paramsQuery={paramsQuery}
          path="/api/v1/brands"
          name="brand"
          params={{ limit: 100, offset: 0 }}
        />
      ),
    },
    {
      id: "role",
      name: "role",
      title: "Vai trò",
      display: pathObj?.pathname?.includes("user"),
      comp: (
        <BtnGroupDynamic
          handlePickOptionSearch={handlePickOptionSearch}
          paramsQuery={paramsQuery}
          path="/api/v1/roles"
          name="role"
          params={{ limit: 100, offset: 0 }}
        />
      ),
    },
    pathObj?.pathname?.includes("user") && {
      id: "user-employee-warehouse",
      title: "Kho",
      name: "warehouse",
      display: true,
      isShowAll: true,
      comp: (
        <BtnGroupDynamic
          handlePickOptionSearch={handlePickOptionSearch}
          paramsQuery={paramsQuery}
          path="/api/v1/warehouses"
          name="warehouse"
          params={{ limit: 100, skip: 0 }}
        />
      ),
    },
    {
      id: "status",
      name: "status",
      title: "Trạng thái",
      display:
        pathObj?.pathname?.includes("debt") &&
        ((["debt-sales"].includes(option?.tab) && ["all", "overdue"].includes(option?.subTab)) || ["internal-import", "debt-sales"]?.includes(option?.subTab)),
      data: [
        { _id: STATUS.PAID, name: "Đã thanh toán" },
        { _id: STATUS.UNPAID, name: "Chưa thanh toán" },
      ],
    },
    {
      id: "warehouseOut",
      name: "warehouseOut",
      title: "Kho Xuất",
      isShowAll: true,
      display:
        pathObj?.pathname?.includes("debt") &&
        ((["debt-sales"].includes(option?.tab) && debtSalesSubTabsWarehouseInOut.includes(option?.subTab)) || ["debt-warehouse", "internal-import"]?.includes(option?.subTab)),
      comp: (
        <BtnGroupDynamic
          handlePickOptionSearch={handlePickOptionSearch}
          paramsQuery={paramsQuery}
          path="/api/v1/warehouses"
          name="warehouse"
          params={{ limit: 100, offset: 0 }}
        />
      ),
    },
    {
      id: "warehouseDebt",
      name: "warehouseDebt",
      title: "Kho Nhập",
      display:
        pathObj?.pathname?.includes("debt") &&
        ((["debt-sales"].includes(option?.tab) && debtSalesSubTabsWarehouseInOut.includes(option?.subTab)) || ["debt-warehouse", "internal-import"]?.includes(option?.subTab)),
      comp: (
        <BtnGroupDynamic
          handlePickOptionSearch={handlePickOptionSearch}
          paramsQuery={paramsQuery}
          path="/api/v1/warehouses"
          name="warehouseDebt"
          params={{ limit: 100, offset: 0 }}
        />
      ),
    },
  ].filter(Boolean);

  const handleSetPop = (placement) => (event) => {
    setPopperProps({
      open: !popperProps.open,
      anchorEl: event.currentTarget,
      placement,
    });
  };

  const handleClosePopper = () => {
    setPopperProps((prev) => ({ ...prev, open: false }));
  };

  const hasActiveFilters = useMemo(() => {
    const fromParams =
      paramsQuery &&
      typeof paramsQuery === "object" &&
      Object.keys(paramsQuery).some((k) => {
        const val = paramsQuery[k];
        return val !== undefined && val !== null && val !== "";
      });
    return Boolean(fromParams || option?.whouseId);
  }, [paramsQuery, option?.whouseId]);

  const handleClearFilters = () => {
    setParamsQuery({});
    setOption((prev) => {
      if (!prev?.whouseId) return prev;
      const { whouseId: _removed, ...rest } = prev;
      return rest;
    });
  };

  return (
    <ClickAwayListener onClickAway={handleClosePopper}>
      <Stack sx={{ display: "inline-block" }}>
        <Button
          size="small"
          variant="outlined"
          sx={{ height: "100%", maxHeight: "36.5px" }}
          startIcon={<Tune />}
          onClick={handleSetPop("bottom-end")}
        >
          Lọc
        </Button>
        <Popper
          disablePortal
          sx={{
            zIndex: 1200,
            maxWidth: "900px",
            width: "90%",
            maxHeight: "330px",
            height: "auto",
            overflow: "auto",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
          }}
          open={popperProps.open}
          anchorEl={popperProps.anchorEl}
          placement={popperProps.placement}
          transition
        >
          {({ TransitionProps }) => (
            <Fade
              {...TransitionProps}
              timeout={350}
            >
              <Paper sx={{ p: "12px", display: "flex", flexDirection: "column" }}>
                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  sx={{ pb: 1, mb: 1, borderBottom: "1px solid var(--light-grey)" }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    startIcon={<Clear />}
                    disabled={!hasActiveFilters}
                    onClick={handleClearFilters}
                    sx={{ fontSize: "12px" }}
                  >
                    Xóa bộ lọc
                  </Button>
                </Stack>
                {AdvanceFilterList?.map((v) => {
                  const tatCaKey = advanceFilterTatCaParamKey(v);
                  return (
                    [undefined, true]?.includes(v?.display) && (
                      <Stack
                        direction="column"
                        key={v.id}
                        spacing={1}
                        mb={1.5}
                        pd={1.5}
                      >
                        <Typography sx={{ fontWeight: "bold", fontSize: "12px" }}>{v.title}</Typography>
                        <Stack
                          direction="row"
                          gap={1}
                        >
                          {(v.name !== "warehouse" || v.isShowAll) && (
                            <Button
                              onClick={() =>
                                handlePickOptionSearch({
                                  name: tatCaKey,
                                  value: ORDER_SOURCE_TYPE.All,
                                })
                              }
                              sx={{
                                border: isTatCaActiveForFilterGroup(tatCaKey) ? "1px solid var(--confirm-txt)" : "1px solid var(--light-grey)",
                                color: isTatCaActiveForFilterGroup(tatCaKey) ? "var(--confirm-txt)" : "var(--black)",
                                display: "inline",
                                borderRadius: "4px",
                                fontSize: "12px",
                              }}
                            >
                              Tất cả
                            </Button>
                          )}
                          {!v?.comp &&
                            v?.data?.map((val) => (
                              <Button
                                key={val?._id}
                                onClick={() =>
                                  handlePickOptionSearch({
                                    name: v?.name,
                                    value: val?._id,
                                  })
                                }
                                disabled={(v.name === "provider" && paramsQuery?.warehouseOut) || (v.name === "warehouseOut" && paramsQuery?.provider)}
                                sx={{
                                  border: [val?._id]?.includes(paramsQuery?.[v?.name]) ? "1px solid var(--confirm-txt)" : "1px solid var(--light-grey)",
                                  color: [val?._id]?.includes(paramsQuery?.[v?.name]) ? "var(--confirm-txt)" : "var(--black)",
                                  display: "inline",
                                  p: "6px 12px !important",
                                  borderRadius: "4px",
                                  fontSize: "12px",
                                  opacity: (v.name === "provider" && paramsQuery?.warehouseOut) || (v.name === "warehouseOut" && paramsQuery?.provider) ? 0.5 : 1,
                                  pointerEvents: (v.name === "provider" && paramsQuery?.warehouseOut) || (v.name === "warehouseOut" && paramsQuery?.provider) ? "none" : "auto",
                                }}
                              >
                                {val?.name}
                              </Button>
                            ))}
                          {v?.comp ?? ""}
                        </Stack>
                        <Divider sx={{ color: "var(--light-grey)" }} />
                      </Stack>
                    )
                  );
                })}
              </Paper>
            </Fade>
          )}
        </Popper>
      </Stack>
    </ClickAwayListener>
  );
}
